import { c as _c } from "react/compiler-runtime";
import type { KeyWith } from '@/lib/piano/keyboard';
import { isOctaveStart } from '@/lib/piano/keyboard/keyboard-geometry';
import { cn } from '@/lib/utils';
import type { KeyAdjustmentPayload } from './AdjustmentSheet.types';
import { AdjustmentValue } from './AdjustmentValue';
export interface AdjustmentSheetTableRowProps {
  hasWippenAssistSpringDesign: boolean;
  isBlackKey: boolean;
  item: KeyWith<KeyAdjustmentPayload | null>;
}

/*
 * On paper the rows close up to the 28 px of the print mockup: at their screen
 * height of 42 px the sheet ran to five pages where the mockup takes four.
 */
const cellClassName = 'h-[42px] px-3 align-middle font-mono text-sm print:h-7 print:px-1.5 print:text-[13px]';

/*
 * On paper the pills flatten to plain text, and the change to one weight ran
 * into the reading of the next: each column after the note is ruled off from
 * the one before it. On screen the pills keep them apart.
 */
const ruledCellClassName = `${cellClassName} print:border-l`;
const Adjustment = t0 => {
  const $ = _c(3);
  const {
    actualValue,
    targetValue
  } = t0;
  let t1;
  if ($[0] !== actualValue || $[1] !== targetValue) {
    t1 = <td className={ruledCellClassName}><div className="flex items-baseline gap-2"><AdjustmentValue actualValue={actualValue} targetValue={targetValue} /></div></td>;
    $[0] = actualValue;
    $[1] = targetValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/*
 * Each C opens an octave, which the printed sheet rules off, as a score does.
 * The rules between keys are drawn by the rows and this one by the cells: in a
 * collapsed table, at equal width, a cell's border wins over a row's, where
 * between two cells the one above wins. A rule half a pixel wider would not
 * do, for Chrome rounds it down to a whole pixel on paper.
 */
export const AdjustmentSheetTableRow: React.FC<AdjustmentSheetTableRowProps> = t0 => {
  const $ = _c(31);
  const {
    hasWippenAssistSpringDesign,
    isBlackKey,
    item
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = cn("border-b border-border even:bg-secondary", isOctaveStart(item) && "print:[&>td]:border-t print:[&>td]:border-t-foreground");
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.number) {
    t2 = <td className={`${cellClassName} text-xs text-ptl-ink-3`}>{item.number}</td>;
    $[2] = item.number;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item) {
    t3 = cn(cellClassName, isOctaveStart(item) && "print:font-semibold");
    $[4] = item;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isBlackKey || $[7] !== item.name) {
    t4 = isBlackKey ? <span className="inline-flex rounded-[3px] bg-ptl-key-black px-1.5 py-px text-[12.5px] text-ptl-key-white">{item.name}</span> : item.name;
    $[6] = isBlackKey;
    $[7] = item.name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <td className={t3}>{t4}</td>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = item.payload?.actualFrontWeight ?? null;
  const t7 = item.payload?.targetFrontWeight ?? null;
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <Adjustment actualValue={t6} targetValue={t7} />;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = item.payload?.actualStrikeWeight ?? null;
  const t10 = item.payload?.targetStrikeWeight ?? null;
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <Adjustment actualValue={t9} targetValue={t10} />;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== hasWippenAssistSpringDesign || $[19] !== item.payload?.actualSupportSpringBalanceWeight || $[20] !== item.payload?.targetSupportSpringBalanceWeight) {
    t12 = hasWippenAssistSpringDesign && <Adjustment actualValue={item.payload?.actualSupportSpringBalanceWeight ?? null} targetValue={item.payload?.targetSupportSpringBalanceWeight ?? null} />;
    $[18] = hasWippenAssistSpringDesign;
    $[19] = item.payload?.actualSupportSpringBalanceWeight;
    $[20] = item.payload?.targetSupportSpringBalanceWeight;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <td className={`${ruledCellClassName} ptl-print-only text-center`}><span className="inline-block size-[15px] rounded-[2px] border-[1.5px] border-foreground align-middle" /></td>;
    t14 = <td className={`${ruledCellClassName} ptl-print-only`} />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t1 || $[25] !== t11 || $[26] !== t12 || $[27] !== t2 || $[28] !== t5 || $[29] !== t8) {
    t15 = <tr className={t1}>{t2}{t5}{t8}{t11}{t12}{t13}{t14}</tr>;
    $[24] = t1;
    $[25] = t11;
    $[26] = t12;
    $[27] = t2;
    $[28] = t5;
    $[29] = t8;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};